import { BrendaAddressEntity, CustomerAddressProps } from '~/types'

/**
 * Calculate distance between points
 */
export const haversineDistance = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
) => {
  const toRad = (x: number) => (x * Math.PI) / 180
  const R = 6371 // Radius of the Earth in km
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon1 - lon2)
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

// Create a single address line string from the brenda address entity
export const formatSuggestionLabel = (e: BrendaAddressEntity) => {
  const parts = [
    e.address1,
    e.address2,
    e.address3,
    e.city,
    e.county,
    e.postcode,
  ].filter(Boolean)

  return parts.join(', ')
}

// Map the brenda address entity to our custom address object
export const mapBrendaEntityToAddressObject = (
  e: BrendaAddressEntity
): CustomerAddressProps => ({
  line_1: e.address1,
  line_2: e.address2 ?? null,
  line_3: e.address3 ?? null,
  town_or_city: e.city ?? null,
  county: e.county ?? null,
  postcode: e.postcode,
})

// Basic UK postcode validation
export const normalizeUkPostcode = (value: string) =>
  value.trim().toUpperCase().replace(/\s+/g, ' ')

export const isValidUkPostcode = (value: string) => {
  const pc = normalizeUkPostcode(value)

  // Standard UK postcode patterns (incl. GIR 0AA)
  // Examples: SW1A 1AA, M1 1AE, B33 8TH, CR2 6XH, DN55 1PT, W1A 0AX, EC1A 1BB
  const UK_POSTCODE_REGEX =
    /^(GIR 0AA|(?:[A-PR-UWYZ][0-9][0-9]?[A-HJKPSTUW]?|[A-PR-UWYZ][A-HK-Y][0-9][0-9]?[ABEHMNPRVWXY]?|[A-PR-UWYZ][0-9][A-HJKPSTUW]|[A-PR-UWYZ][A-HK-Y][0-9][A-HJKPSTUW])\s?[0-9][ABD-HJLNP-UW-Z]{2})$/i

  // Allow optional space before the inward code
  return UK_POSTCODE_REGEX.test(pc.replace(/\s+/g, ''))
    ? UK_POSTCODE_REGEX.test(pc.replace(/\s+/g, ' '))
    : UK_POSTCODE_REGEX.test(pc)
}
